import { unsignedKW, isIdlePower } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { scada } from "../scadaColors";

/**
 * PV array — renders an isometric-feeling tilted-panel field with a sun
 * glyph and animated rays during generation. Includes a capacity-factor
 * mini bar so the operator can tell "are we hitting nameplate?" at a glance.
 */
export function SolarArray({
  cx, cy, active_W, today_kWh, rated_kWp, label, detail = true,
}: {
  cx: number; cy: number; active_W: number; today_kWh: number; rated_kWp: number;
  /** Optional per-array tag — shown only on multi-array sites. */
  label?: string;
  /** Summary mode (false) hides the capacity-factor strip. */
  detail?: boolean;
}) {
  const t = useT();
  const YELLOW = scada.solar();
  // Unified idle noise floor — a PV inverter trickling standby/leakage
  // current must not animate as if it were generating.
  const generating = !isIdlePower(active_W);
  const cap = Math.round((active_W / Math.max(1, rated_kWp * 1000)) * 100);

  return (
    <g style={{ cursor: "pointer" }}>
      {/* Drop shadow */}
      <ellipse cx={cx} cy={cy + 50} rx={56} ry={4.5} fill="var(--scada-shadow)" opacity={0.5} />

      {/* Background plate */}
      <rect x={cx - 64} y={cy - 50} width={128} height={100} rx={12}
        fill="var(--surface)" stroke={YELLOW} strokeOpacity={0.55} strokeWidth={1.3} />
      <rect x={cx - 64} y={cy - 50} width={128} height={14} rx={12}
        fill={YELLOW} fillOpacity={0.10} />
      <text x={cx - 56} y={cy - 41} fontSize={7.5} fontWeight={800} fill={YELLOW} letterSpacing={1.4}>
        {`${t("monitor.scada.node.generation")} · ${t("monitor.scada.node.pv")}`}
      </text>
      <circle cx={cx + 54} cy={cy - 43} r={2} fill={YELLOW} />

      {/* Sun in the top-right corner of the plate */}
      <g transform={`translate(${cx + 36} ${cy - 22})`}>
        <circle cx={0} cy={0} r={8} fill={YELLOW} opacity={0.92} />
        {[...Array(8)].map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          return (
            <line key={i}
              x1={Math.cos(a) * 11} y1={Math.sin(a) * 11}
              x2={Math.cos(a) * 16} y2={Math.sin(a) * 16}
              stroke={YELLOW} strokeWidth={1.4} strokeLinecap="round"
              opacity={generating ? 0.9 : 0.4}>
              {generating && (
                <animate attributeName="opacity" values="0.9;0.35;0.9"
                  dur="2.4s" repeatCount="indefinite" begin={`${i * 0.15}s`} />
              )}
            </line>
          );
        })}
      </g>

      {/* Three tilted panels in a row, isometric-ish (skewX trick) */}
      <g transform={`translate(${cx - 32} ${cy + 6})`}>
        {[0, 1, 2].map((i) => (
          <g key={i} transform={`translate(${i * 22} 0) skewX(-18)`}>
            {/* Panel face */}
            <rect x={-9} y={-12} width={18} height={26} rx={1}
              fill="var(--surface-2)" stroke={YELLOW} strokeWidth={1.1} />
            {/* Cell grid 3×4 */}
            {[1, 2, 3].map((r) => (
              <line key={`r${r}`} x1={-9} y1={-12 + r * 6.5} x2={9} y2={-12 + r * 6.5}
                stroke={YELLOW} strokeOpacity={0.45} strokeWidth={0.5} />
            ))}
            {[1, 2].map((c) => (
              <line key={`c${c}`} x1={-9 + c * 6} y1={-12} x2={-9 + c * 6} y2={14}
                stroke={YELLOW} strokeOpacity={0.45} strokeWidth={0.5} />
            ))}
            {generating && (
              <line x1={-7} y1={-10} x2={-2} y2={-4}
                stroke={YELLOW} strokeWidth={1.4}>
                <animate attributeName="opacity" values="0.9;0.3;0.9" dur="2s" repeatCount="indefinite" />
              </line>
            )}
            {/* Mounting post (un-skewed) */}
            <line x1={0} y1={14} x2={0} y2={20} stroke={YELLOW} strokeWidth={1} />
          </g>
        ))}
      </g>

      {/* Ground line */}
      <line x1={cx - 50} y1={cy + 28} x2={cx + 26} y2={cy + 28}
        stroke={YELLOW} strokeWidth={1.3} />

      {/* Capacity factor strip — secondary, collapsed in Summary mode. */}
      {detail && (
        <g transform={`translate(${cx - 56} ${cy + 32})`}>
          {/* Native SVG tooltip expanding the abbreviation (OVW-12). */}
          <title>{t("monitor.scada.node.capFactorTip")}</title>
          <rect width={112} height={16} rx={3} fill="var(--surface-3)" stroke="var(--edge)" strokeWidth={0.6} />
          <text x={6} y={11} fontSize={7.5} fill="var(--text-faint)" letterSpacing={0.3}>{t("monitor.scada.node.capFactor")}</text>
          <rect x={56} y={4} width={42} height={8} rx={2} fill="var(--surface-2)" stroke="var(--edge)" strokeWidth={0.5} />
          <rect x={56} y={4} width={42 * (cap / 100)} height={8} rx={2} fill={YELLOW} opacity={0.85} />
          <text x={104} y={11} textAnchor="end" fontSize={9} fontWeight={700} fill="var(--text)" className="num">
            {cap}%
          </text>
        </g>
      )}

      {/* Footer */}
      <text x={cx} y={cy + 64} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text)">
        {label ?? t("monitor.scada.node.solarPv")}
      </text>
      <text x={cx} y={cy + 78} textAnchor="middle" fontSize={13} fontWeight={800}
        className="num" fill={YELLOW}>
        {unsignedKW(active_W)} kW
      </text>
      <text x={cx} y={cy + 90} textAnchor="middle" fontSize={9} fill="var(--text-muted)">
        {t("monitor.now.kwhToday", { kwh: today_kWh.toFixed(0) })} · {rated_kWp.toFixed(0)} kWp
      </text>
    </g>
  );
}
